import { useEffect, useState } from 'react';
import { first } from 'lodash';
import { isRemoteAndNotDisconnected } from 'App/Activity/components/Utility';
import { PowerState, SessionSupport } from 'App/Activity/Network/ActionsUtil';
import { isDesktopSession } from 'App/Activity/ResourceActionHandler';
import {
	DesktopAction,
	LaunchAction,
	MachineAction,
	MachineData,
	Session,
	SessionAction,
	SessionConnectionState,
	UNKNOWN_STATE,
	useResourceManagerContext,
} from 'App/Activity/ResourceManagerContext';
import { useTransitioningStatus } from 'App/Activity/useTransitioningStatus';
import { environment } from 'Environment';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { useUserContext } from 'Workspace/UserContext';

interface SessionActionsProps {
	session?: Session;
	machine?: MachineData;
	resource?: Resource;
}

export const useDesktopActions = ({
	session,
	machine,
	resource,
}: SessionActionsProps) => {
	const [actions, setActions] = useState<DesktopAction[]>([]);
	const { deviceId } = useResourceManagerContext();
	const combinedDeviceId =
		environment?.nativeCapabilities?.platform?.deviceId || deviceId;
	const transitioningStatus = useTransitioningStatus(
		session || ({ machineData: machine } as Session)
	);
	const isPowerManagementControlsFeatureEnabled = useFeatureCanary(
		FeatureFlag.WSUIPowerManagementControls
	);
	const { powerManagementControls } = useUserContext();

	useEffect(() => {
		if (transitioningStatus === UNKNOWN_STATE) {
			const desktopActions: DesktopAction[] = [];
			if (canResume(machine)) {
				desktopActions.push(LaunchAction.RESUME);
			} else {
				canDisconnect(session) && desktopActions.push(SessionAction.DISCONNECT);
				canReconnect(session) && desktopActions.push(LaunchAction.RECONNECT);
				canLogout(session) && desktopActions.push(SessionAction.LOGOUT);
				canRestart(session, resource) && desktopActions.push(LaunchAction.RESTART);
				session &&
					canSessionHandoff(session, combinedDeviceId) &&
					desktopActions.push(LaunchAction.TRANSFER); // TODO: Need to be corrected as it will be true for null session
				canRestart(session, resource) &&
					canShutDown(machine) &&
					(!isPowerManagementControlsFeatureEnabled ||
						powerManagementControls?.shutdown) &&
					desktopActions.push(MachineAction.SHUTDOWN);
				canRestart(session, resource) &&
					canPowerOff(machine) &&
					(!isPowerManagementControlsFeatureEnabled ||
						powerManagementControls?.powerOff) &&
					desktopActions.push(MachineAction.POWEROFF);
				canHibernate(machine) &&
					(!isPowerManagementControlsFeatureEnabled ||
						powerManagementControls?.hibernate) &&
					desktopActions.push(MachineAction.HIBERNATE);
			}
			setActions(desktopActions);
		} else {
			setActions([]);
		}
	}, [
		transitioningStatus,
		session,
		machine,
		resource,
		deviceId,
		combinedDeviceId,
		isPowerManagementControlsFeatureEnabled,
		powerManagementControls,
	]);

	return actions;
};

//allow-unused-export
export function canDisconnect(session: Session) {
	return session?.connectionState === SessionConnectionState.CONNECTED;
}

export function canReconnect(session: Session) {
	return (
		session?.connectionState === SessionConnectionState.DISCONNECTED &&
		session?.machineData?.powerState !== PowerState.SUSPENDED
	);
}

//allow-unused-export
export function canLogout(session: Session) {
	return (
		session?.connectionState === SessionConnectionState.CONNECTED ||
		(session?.logoffDisconnectedSupported &&
			session?.connectionState === SessionConnectionState.DISCONNECTED)
	);
}

const isMachinePoweredOff = (machine: MachineData) =>
	machine?.powerState === PowerState.OFF;

const canShutDown = (machine: MachineData) =>
	canPerformSingleSessionMachineAction(machine, 'shutDown');

const canPowerOff = (machine: MachineData) =>
	canPerformSingleSessionMachineAction(machine, 'turnOff');

const canHibernate = (machine: MachineData) =>
	canPerformSingleSessionMachineAction(machine, 'suspend');

function canPerformSingleSessionMachineAction(
	machine: MachineData,
	action: 'shutDown' | 'turnOff' | 'suspend'
) {
	const actionSupportMap = {
		shutDown: machine?.shutDownSupported,
		turnOff: machine?.turnOffSupported,
		suspend: machine?.suspendSupported,
	};
	const isMachineOn = !isMachinePoweredOff(machine);
	const isSingleSessionSupported =
		machine?.sessionSupport === SessionSupport.SINGLE_SESSION;
	const isActionSupported = actionSupportMap[action];

	return isMachineOn && isSingleSessionSupported && isActionSupported;
}
function canRestart(session: Session, resource?: Resource) {
	return (
		(isDesktopSession(session) &&
			!!first(session?.applications)?.resource?.poweroffurl) ||
		(!!resource?.isdesktop && !!resource?.poweroffurl)
	);
}

function canResume(machine: MachineData) {
	return machine?.powerState === PowerState.SUSPENDED;
}

function canSessionHandoff(session: Session, deviceId: string) {
	return isRemoteAndNotDisconnected(session, deviceId);
}
